// src/api/groups.ts
import axiosClient from './axiosClient';
import type {
    DirectoryUser,
    GroupDetails,
    GroupMember,
    GroupRole,
    LeaveGroupResult,
} from '../types';

/**
 * Ответ может прийти как «плоским» JSON (`{id, title, ...}`), так и в
 * JSON:API-обёртке (`{data: {id, attributes}}`) — парсер понимает оба вида,
 * чтобы диалог не зависел от сериализации бэкенда.
 */
interface RawPayload {
    id?: unknown;
    attributes?: unknown;
    [key: string]: unknown;
}

const asRecord = (raw: unknown): RawPayload | null => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
    return raw as RawPayload;
};

/** Достаёт из ответа сами данные: снимает обёртку `data` и `attributes`. */
const asPayload = (raw: unknown): RawPayload | null => {
    const record = asRecord(raw);
    if (!record) return null;

    const data = asRecord(record.data) ?? record;
    const attributes = asRecord(data.attributes);

    return attributes ? { ...attributes, id: data.id ?? attributes.id } : data;
};

const asString = (value: unknown, fallback: string): string =>
    typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;

const asNullableString = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

const asInt = (value: unknown, fallback: number): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

const asBoolean = (value: unknown, fallback: boolean): boolean =>
    typeof value === 'boolean' ? value : fallback;

/** Незнакомую роль считаем обычным участником: прав она не даёт. */
export const parseGroupRole = (value: unknown): GroupRole =>
    value === 'admin' ? 'admin' : 'member';

const parseMember = (raw: unknown): GroupMember | null => {
    const member = asPayload(raw);
    if (!member) return null;

    const userId = asString(member.userId, '');
    if (userId === '') return null;

    return {
        userId,
        fullName: asString(member.fullName, 'Участник'),
        email: asString(member.email, ''),
        role: parseGroupRole(member.role),
        joinedAt: asNullableString(member.joinedAt),
        isOnline: asBoolean(member.isOnline, false),
    };
};

const parseMembers = (raw: unknown): GroupMember[] => {
    if (!Array.isArray(raw)) return [];

    return raw
        .map(parseMember)
        .filter((member): member is GroupMember => member !== null);
};

/**
 * Разбор ответа `GET/PATCH /api/groups/{id}` и всех операций над составом.
 *
 * `memberCount` берём из ответа, но если бэкенд его не прислал — считаем по
 * списку участников, чтобы подзаголовок в шапке не врал.
 */
export const parseGroupDetails = (raw: unknown): GroupDetails | null => {
    const group = asPayload(raw);
    if (!group) return null;

    const id = asString(group.id, '');
    if (id === '') return null;

    const members = parseMembers(group.members);

    return {
        id,
        title: asString(group.title, 'Групповой чат'),
        isGroup: asBoolean(group.isGroup, true),
        createdAt: asString(group.createdAt, ''),
        memberCount: asInt(group.memberCount, members.length),
        currentUserRole: parseGroupRole(group.currentUserRole),
        members,
    };
};

/** Разбор ответа `POST /api/groups/{id}/leave`. */
export const parseLeaveResult = (raw: unknown): LeaveGroupResult => {
    const payload = asPayload(raw);

    return {
        left: asBoolean(payload?.left, true),
        groupDeleted: asBoolean(payload?.groupDeleted, false),
    };
};

const parseDirectoryUser = (raw: unknown): DirectoryUser | null => {
    const user = asPayload(raw);
    if (!user) return null;

    const id = asString(user.id, '');
    if (id === '') return null;

    return {
        id,
        fullName: asString(user.fullName, 'Пользователь'),
        email: asString(user.email, ''),
    };
};

/** Состав группы и права текущего пользователя. */
export const fetchGroup = async (groupId: string): Promise<GroupDetails | null> => {
    const response = await axiosClient.get<unknown>(`/groups/${groupId}`);

    return parseGroupDetails(response.data);
};

/** Переименование группы — только админ. Возвращает обновлённый состав. */
export const renameGroup = async (
    groupId: string,
    title: string,
): Promise<GroupDetails | null> => {
    const response = await axiosClient.patch<unknown>(`/groups/${groupId}`, {
        title: title.trim(),
    });

    return parseGroupDetails(response.data);
};

/** Добавление участников: уже состоящие в группе бэкенд молча пропускает. */
export const addGroupMembers = async (
    groupId: string,
    userIds: string[],
): Promise<GroupDetails | null> => {
    const response = await axiosClient.post<unknown>(`/groups/${groupId}/members`, {
        userIds,
    });

    return parseGroupDetails(response.data);
};

/** Удаление участника из группы — только админ. */
export const removeGroupMember = async (
    groupId: string,
    userId: string,
): Promise<GroupDetails | null> => {
    const response = await axiosClient.delete<unknown>(
        `/groups/${groupId}/members/${userId}`,
    );

    return parseGroupDetails(response.data);
};

/** Назначение и снятие админа. */
export const setGroupMemberRole = async (
    groupId: string,
    userId: string,
    role: GroupRole,
): Promise<GroupDetails | null> => {
    const response = await axiosClient.patch<unknown>(
        `/groups/${groupId}/members/${userId}`,
        { role },
    );

    return parseGroupDetails(response.data);
};

/** Выход из группы; последний участник удаляет группу целиком. */
export const leaveGroup = async (groupId: string): Promise<LeaveGroupResult> => {
    const response = await axiosClient.post<unknown>(`/groups/${groupId}/leave`);

    return parseLeaveResult(response.data);
};

/** Удаление группы — только админ; бэкенд отвечает 204 без тела. */
export const deleteGroup = async (groupId: string): Promise<void> => {
    await axiosClient.delete(`/groups/${groupId}`);
};

/** Справочник пользователей для выбора новых участников (`GET /api/users`). */
export const fetchDirectoryUsers = async (): Promise<DirectoryUser[]> => {
    const response = await axiosClient.get<unknown>('/users');
    const payload = asRecord(response.data);
    const nested = payload !== null ? payload.data : null;
    const list: unknown[] = Array.isArray(response.data)
        ? response.data
        : Array.isArray(nested)
            ? nested
            : [];

    return list
        .map(parseDirectoryUser)
        .filter((user): user is DirectoryUser => user !== null);
};
